import { plainToClass } from 'class-transformer';
import {
    registerDecorator,
    validate,
    ValidationArguments,
    ValidationOptions,
    ValidatorConstraint,
    ValidatorConstraintInterface
} from 'class-validator';
import { Resolver } from 'react-hook-form';

export declare type ClassType<T> = {
    new (...args: any[]): T;
};

/**
 * Class validator form resolver factory
 * to create resolver for use with react hook form
 *
 * @export
 * @template TFormValues
 * @param {ClassType<TFormValues>} classValidator
 * @returns {Resolver<TFormValues>}
 */
export function classValidatorFormResolverFactory<TFormValues extends Record<string, any> = Record<string, any>>(
    classValidator: ClassType<TFormValues>
): Resolver<TFormValues> {
    return async (data) => {
        const errors = await validate(plainToClass(classValidator, data), {
            forbidUnknownValues: true
        });

        const hasError = errors.length > 0;
        return {
            values: hasError ? {} : data,
            errors: hasError
                ? errors.reduce((allErrors, currentError) => {
                      return {
                          ...allErrors,
                          [currentError.property]: {
                              type: 'validation',
                              // map constraints to error message string
                              message: currentError.constraints
                                  ? Object.values(currentError.constraints).reduce((prev, curr) => prev + '\n' + curr, '')
                                  : undefined
                          }
                      };
                  }, {})
                : {}
        };
    };
}

export function IsMatch(property: string, validationOptions?: ValidationOptions) {
    return (object: any, propertyName: string) => {
        registerDecorator({
            target: object.constructor,
            propertyName,
            options: validationOptions,
            constraints: [property],
            validator: MatchConstraint
        });
    };
}

@ValidatorConstraint({ name: 'Match' })
export class MatchConstraint implements ValidatorConstraintInterface {
    validate(value: any, args: ValidationArguments) {
        const [relatedPropertyName] = args.constraints;
        const relatedValue = (args.object as any)[relatedPropertyName];
        return value === relatedValue;
    }
}

export function IsNotBlank(validationOptions?: ValidationOptions) {
    return function (object: Object, propertyName: string) {
        registerDecorator({
            target: object.constructor,
            propertyName,
            options: validationOptions,
            validator: IsNotBlankConstraint
        });
    };
}

@ValidatorConstraint({ name: 'isNotBlank' })
export class IsNotBlankConstraint implements ValidatorConstraintInterface {
    validate(value: any) {
        return typeof value === 'string' && value.trim().length > 0;
    }
}
